import React from 'react';
import ReactDOM from 'react-dom/client';
// import './App.css'; // App.css는 App.js에서 직접 관리하거나 MUI sx prop으로 대체
import App from './App';
import './i18n'; // i18n 설정 파일 임포트
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// 현대적이고 세련된 테마 생성
let theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#5468FF',
      light: '#8A94FF',
      dark: '#3A4CCA',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#7950F2',
      light: '#9775F8',
      dark: '#6138D0',
      contrastText: '#ffffff',
    },
    success: {
      main: '#00BFA6',
      light: '#33ECCE',
      dark: '#008975',
    },
    error: {
      main: '#F45B69',
      light: '#FF7F8A',
      dark: '#C73747',
    },
    warning: {
      main: '#FF9F1C',
      light: '#FFB84D',
      dark: '#E08300',
    },
    info: {
      main: '#4CC9F0',
      light: '#7AD7F4',
      dark: '#0BA1D9',
    },
    background: {
      default: '#F8FAFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2D3748',
      secondary: '#718096',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.005em',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    subtitle1: {
      letterSpacing: 0,
      lineHeight: 1.5,
      fontSize: '1.125rem',
    },
    subtitle2: {
      letterSpacing: 0,
      lineHeight: 1.5,
      fontSize: '1rem',
    },
    body1: {
      letterSpacing: 0,
      lineHeight: 1.5,
    },
    body2: {
      letterSpacing: 0,
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 1px 3px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.05)',
    '0px 1px 4px rgba(0, 0, 0, 0.05), 0px 4px 10px rgba(0, 0, 0, 0.07)',
    '0px 2px 5px rgba(0, 0, 0, 0.07), 0px 6px 16px rgba(0, 0, 0, 0.1)',
    '0px 4px 8px rgba(0, 0, 0, 0.1), 0px 8px 24px rgba(0, 0, 0, 0.1)',
    ...Array(20).fill('none'), // 나머지는 기본값 사용
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05), 0px 4px 8px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.06), 0px 8px 16px rgba(0, 0, 0, 0.06)',
          },
        },
        contained: {
          '&.MuiButton-containedPrimary': {
            background: 'linear-gradient(135deg, #5468FF 0%, #7950F2 100%)',
          },
          '&.MuiButton-containedSecondary': {
            background: 'linear-gradient(135deg, #7950F2 0%, #9775F8 100%)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'all 0.2s ease-in-out',
            '&.Mui-focused': {
              boxShadow: '0px 0px 0px 3px rgba(84, 104, 255, 0.2)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.10)',
          },
        },
        elevation1: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.05)',
        },
        elevation2: {
          boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.05), 0px 4px 10px rgba(0, 0, 0, 0.07)',
        },
        elevation3: {
          boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.07), 0px 6px 16px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'transparent',
          boxShadow: 'none',
          transition: 'background-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          overflow: 'hidden',
          transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          // 전역적으로 Select 아이콘 색상 조정 (필요에 따라)
          // color: 'inherit', // AppBar의 LanguageSwitcher에서 이미 개별적으로 흰색으로 설정됨
        },
      },
    },
  },
});

// 반응형 폰트 크기 적용
theme = responsiveFontSizes(theme);

// 기본 스타일 추가
const GlobalStyles = {
  body: {
    scrollBehavior: 'smooth',
    overflowX: 'hidden',
  },
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </HelmetProvider>
  </React.StrictMode>
);
